import React, { useEffect, useState } from 'react';
import { Target, Info } from 'lucide-react';
import { crmImportApi, CrmImportCampaign } from '../../services/crmImportApi';

interface Props {
  projectId?: string | null;
  campaignId: string | null;
  onChange: (campaignId: string | null) => void;
}

/**
 * Campaign-sequence-enrollment Phase 5 picker: lets the user tag this import
 * job to a campaign so newly-created SuiteCRM leads/contacts get enrolled
 * into that campaign's active NurtureWorkflow(s) (trigger='lead_created').
 *
 * Hits GET /api/crm-import/campaigns — a thin `integrations`-gated re-export
 * of the campaign list, NOT the `campaigns`-gated GET /api/campaigns/. See
 * CLAUDE.md's Ad Copy dual-gate note: this panel is reachable by anyone with
 * `integrations` access, who may not have `campaigns` — calling the
 * campaigns-gated endpoint here would 403 inside a panel the user can see.
 *
 * Entirely optional. No project selected → shows a hint and leaves
 * campaignId null; the import proceeds exactly as it does today.
 */
const CrmImportCampaignPicker: React.FC<Props> = ({ projectId, campaignId, onChange }) => {
  const [campaigns, setCampaigns] = useState<CrmImportCampaign[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setCampaigns([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    crmImportApi
      .listCampaigns(projectId)
      .then(({ data }) => { if (!cancelled) setCampaigns(data); })
      .catch(() => { if (!cancelled) setCampaigns([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  if (!projectId) {
    return (
      <p className="crm-import-hint">
        <Info size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
        Select a project above to optionally tag this import to a campaign for automatic email-sequence enrollment.
      </p>
    );
  }

  return (
    <div>
      <p className="crm-import-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Target size={15} />
        Attach to campaign <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
      </p>
      <div className="crm-import-actions" style={{ justifyContent: 'flex-start' }}>
        <select
          className="crm-import-select"
          value={campaignId || ''}
          disabled={loading}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">— No campaign (import only) —</option>
          {campaigns.map((c) => (
            <option key={c.campaign_id} value={c.campaign_id}>
              {c.name}{c.project_name ? ` — ${c.project_name}` : ''}
            </option>
          ))}
        </select>
      </div>
      {campaignId && (
        <p className="crm-import-hint">
          Imported contacts with an email address will be automatically enrolled in any active
          email sequence armed for this campaign (trigger: lead created). A large import may take
          several days to fully send, subject to your account's sending limits.
        </p>
      )}
      {!loading && campaigns.length === 0 && (
        <p className="crm-import-hint">No campaigns found for this project.</p>
      )}
    </div>
  );
};

export default CrmImportCampaignPicker;
